import React, { useEffect, useState } from "react";
import PhoneInput from "../PhoneInput";
import { Toaster } from "../../utils/common";
import { normalizeToE164 } from "../../utils/phoneUtils";
import {
  isLoginStaffUser,
  resolveRequired2faPhone,
  resolveRequired2faPhoneFromApi,
} from "../../utils/loginPhone2fa";
import { sendSMSOTP, verifySMSOTP } from "../../utils/smsOtpService";
import { useDispatch } from "react-redux";
import {
  OTP_CHANNEL_EMAIL,
  OTP_CHANNEL_SMS,
  readUserDataFromStorage,
  fetchTwoFactorAuthPreference,
  updateTwoFactorAuthPreference,
} from "../../utils/twoFactorAuthPreference";
import { CLIENT_ACTION, EMPLOYEE_ACTION } from "../../redux/action";
import "./twoFactorAuthModal.css";

const OTP_LENGTH = 6;
const OTP_VALIDITY_SECONDS = 120;

const TwoFactorAuthModal = ({ open, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("settings");
  const [loading, setLoading] = useState(false);
  const [otpChannel, setOtpChannel] = useState(OTP_CHANNEL_EMAIL);
  const [savedOtpChannel, setSavedOtpChannel] = useState(OTP_CHANNEL_EMAIL);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [savedPhoneNumber, setSavedPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [pendingPhone, setPendingPhone] = useState("");
  const [pendingOtpChannel, setPendingOtpChannel] = useState(OTP_CHANNEL_EMAIL);
  const [userData, setUserData] = useState(() => readUserDataFromStorage());
  const [profileLoading, setProfileLoading] = useState(false);
  const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(OTP_VALIDITY_SECONDS);

  const phoneOtpEnabled = otpChannel === OTP_CHANNEL_SMS;
  const userEmail =
    userData?.email || localStorage.getItem("email") || "";

  const resetOtpState = () => {
    setStep("settings");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpError(null);
    setPendingPhone("");
    setPendingOtpChannel(OTP_CHANNEL_EMAIL);
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  useEffect(() => {
    if (!open) {
      resetOtpState();
      return;
    }
    let cancelled = false;

    const local = readUserDataFromStorage();

    setUserData(local);
    const initialPhone = resolveRequired2faPhone(local) || "";
    setPhoneNumber(initialPhone);
    setSavedPhoneNumber(initialPhone);
    setPhoneError(null);
    setProfileLoading(true);
    resetOtpState();

    (async () => {
      try {
        const staff = isLoginStaffUser(local);
        const userId = staff
          ? local.employee_id ||
            local.employeeId ||
            localStorage.getItem("user_id")
          : local.client_id ||
            local.clientId ||
            localStorage.getItem("user_id");

        if (!userId) {
          setOtpChannel(OTP_CHANNEL_EMAIL);
          setSavedOtpChannel(OTP_CHANNEL_EMAIL);
          return;
        }

        const [fresh, pref] = await Promise.all([
          staff
            ? dispatch(EMPLOYEE_ACTION.getEmployee(userId)).unwrap()
            : dispatch(CLIENT_ACTION.getClientById(userId)).unwrap(),
          fetchTwoFactorAuthPreference(),
        ]);

        if (cancelled) return;

        const profileObj =
          fresh && typeof fresh === "object" && !Array.isArray(fresh)
            ? fresh
            : {};

        const prefPhone = resolveRequired2faPhoneFromApi(
          profileObj,
          pref || {},
          staff,
        );

        const mergedForDecision = {
          ...local,
          ...profileObj,
          ...(prefPhone
            ? staff
              ? { personal_phone: prefPhone, personalPhone: prefPhone }
              : { mfa_phone: prefPhone, mfaPhone: prefPhone }
            : {}),
        };

        const rawPref = String(
          pref?.otp_channel ??
            pref?.otpChannel ??
            pref?.two_factor_channel ??
            pref?.twoFactorChannel ??
            "",
        )
          .trim()
          .toLowerCase();

        let channel =
          rawPref === "sms" || rawPref === "phone"
            ? OTP_CHANNEL_SMS
            : rawPref === "email"
              ? OTP_CHANNEL_EMAIL
              : null;

        if (!channel) {
          channel = prefPhone
            ? OTP_CHANNEL_SMS
            : OTP_CHANNEL_EMAIL;
        }

        setOtpChannel(channel);
        setSavedOtpChannel(channel);
        setUserData({
          ...mergedForDecision,
          otp_channel: channel,
          otpChannel: channel,
          two_factor_channel: channel,
        });
        const resolvedPhone = prefPhone || "";
        setPhoneNumber(resolvedPhone);
        setSavedPhoneNumber(resolvedPhone);
      } catch {
        if (!cancelled) {
          const fallbackPhone = resolveRequired2faPhone(local) || "";
          const fallbackChannel = fallbackPhone
            ? OTP_CHANNEL_SMS
            : OTP_CHANNEL_EMAIL;
          setOtpChannel(fallbackChannel);
          setSavedOtpChannel(fallbackChannel);
          setPhoneNumber(fallbackPhone);
          setSavedPhoneNumber(fallbackPhone);
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  useEffect(() => {
    if (step !== "otp" || resendTimer === 0) return undefined;
    const interval = setInterval(() => {
      setResendTimer((value) => value - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const handleTogglePhoneOtp = (enabled) => {
    setOtpChannel(enabled ? OTP_CHANNEL_SMS : OTP_CHANNEL_EMAIL);
    setPhoneError(null);
  };

  const getExistingVerifiedPhone = () =>
    normalizeToE164(savedPhoneNumber) ||
    normalizeToE164(resolveRequired2faPhone(userData)) ||
    "";

  const resolvePhoneToSave = () => {
    const trimmed = String(phoneNumber || "").trim();
    let phoneToSave;

    if (phoneOtpEnabled) {
      if (!trimmed) {
        setPhoneError("Please enter your phone number");
        return null;
      }
      const normalized = normalizeToE164(phoneNumber);
      if (!normalized || !normalized.startsWith("+")) {
        setPhoneError("Please enter a valid mobile number with country code");
        return null;
      }
      phoneToSave = normalized;
    } else if (trimmed) {
      const normalized = normalizeToE164(phoneNumber);
      if (!normalized || !normalized.startsWith("+")) {
        setPhoneError("Please enter a valid mobile number with country code");
        return null;
      }
      phoneToSave = normalized;
    } else {
      const existing = getExistingVerifiedPhone();
      phoneToSave = existing || "";
    }

    return phoneToSave;
  };

  const isChannelChanged = otpChannel !== savedOtpChannel;
  const normalizedInputPhone = phoneNumber
    ? normalizeToE164(phoneNumber) || ""
    : "";
  const existingVerifiedPhone = getExistingVerifiedPhone();
  const isPhoneChanged = Boolean(
    normalizedInputPhone && normalizedInputPhone !== existingVerifiedPhone,
  );
  const requiresOtpBeforeSave =
    isChannelChanged ||
    isPhoneChanged ||
    (phoneOtpEnabled && !existingVerifiedPhone);

  const phoneNeedsVerification = (phoneToSave) => {
    if (isChannelChanged) return true;
    if (phoneToSave && phoneToSave !== getExistingVerifiedPhone()) return true;
    if (phoneOtpEnabled && !getExistingVerifiedPhone()) return true;
    return false;
  };

  const persistPreferences = async (phoneToSave, channel) => {
    const result = await updateTwoFactorAuthPreference({
      otpChannel: channel,
      phone: phoneToSave,
      userData,
    });

    if (!result.success) {
      Toaster.error(
        result.message || "Failed to update two-factor authentication",
      );
      return false;
    }

    setUserData(result.userData || readUserDataFromStorage());
    setSavedOtpChannel(channel);
    if (phoneToSave) {
      setSavedPhoneNumber(phoneToSave);
    }
    Toaster.success(result.message || "Two-factor authentication updated");
    onSaved?.(result.userData);
    onClose?.();
    return true;
  };

  const sendOtpToPhone = async (normalizedPhone) => {
    const result = await sendSMSOTP(normalizedPhone, userEmail || null, {
      requireSms: true,
    });
    if (!result.success) {
      throw new Error(result.message || "Failed to send OTP");
    }
    setPendingPhone(normalizedPhone);
    setStep("otp");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpError(null);
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  const handleSave = async () => {
    const phoneToSave = resolvePhoneToSave();
    if (phoneToSave === null) return;

    if (phoneNeedsVerification(phoneToSave)) {
      if (!phoneToSave) {
        setPhoneError("A phone number is required to receive verification OTP");
        return;
      }
      setPendingOtpChannel(otpChannel);
      setLoading(true);
      try {
        await sendOtpToPhone(phoneToSave);
      } catch (error) {
        setPhoneError(error?.message || "Failed to send OTP. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      await persistPreferences(phoneToSave, otpChannel);
    } catch (error) {
      Toaster.error(
        error?.message || "Failed to update two-factor authentication",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingPhone || resendTimer > 0) return;
    setLoading(true);
    setOtpError(null);
    try {
      await sendOtpToPhone(pendingPhone);
    } catch (error) {
      setOtpError(error?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (event, index) => {
    const value = event.target.value;
    if (value && Number.isNaN(Number(value))) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setOtpError(null);
    if (value && index < OTP_LENGTH - 1) {
      document.getElementById(`two-factor-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`two-factor-otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = new Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((digit, index) => {
      next[index] = digit;
    });
    setOtp(next);
    setOtpError(null);
    document
      .getElementById(
        `two-factor-otp-${Math.min(pasted.length, OTP_LENGTH) - 1}`,
      )
      ?.focus();
  };

  const handleVerifyAndSave = async () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      setOtpError("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const verifyResult = await verifySMSOTP(
        code,
        pendingPhone,
        userEmail || null,
      );
      if (!verifyResult.success) {
        setOtpError(verifyResult.message || "Invalid OTP. Please try again.");
        return;
      }

      await persistPreferences(pendingPhone, pendingOtpChannel);
    } catch (error) {
      setOtpError(
        error?.message || "Failed to verify OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSettings = () => {
    setStep("settings");
    setOtp(new Array(OTP_LENGTH).fill(""));
    setOtpError(null);
    setResendTimer(OTP_VALIDITY_SECONDS);
  };

  const handleClose = () => {
    resetOtpState();
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="two-factor-modal-overlay" onClick={handleClose}>
      <div className="two-factor-modal" onClick={(e) => e.stopPropagation()}>
        {step === "settings" ? (
          <>
            <h2 className="two-factor-modal-title">Two Factor Authentication</h2>
            <p className="two-factor-modal-subtitle">
              Secure your account with an additional verification step. A
              verification code will be sent to your registered phone number when
              you sign in.
            </p>

            <div className="two-factor-step-row">
              <span className="two-factor-step-label">2 - Step Verification</span>
              <button
                type="button"
                role="switch"
                aria-checked={phoneOtpEnabled}
                aria-label="Enable phone OTP"
                className={`two-factor-switch${phoneOtpEnabled ? " is-on" : ""}`}
                onClick={() => handleTogglePhoneOtp(!phoneOtpEnabled)}
                disabled={loading || profileLoading}
              >
                <span className="two-factor-switch-thumb" />
              </button>
            </div>

            {profileLoading ? (
              <p className="two-factor-loading">Loading account details…</p>
            ) : (
              <div className="two-factor-phone-block">
                <PhoneInput
                  labelname="Phone"
                  value={phoneNumber}
                  onChange={(value) => {
                    setPhoneNumber(value || "");
                    setPhoneError(null);
                  }}
                  error={phoneError !== null}
                  errmsg={phoneError}
                  required={phoneOtpEnabled}
                  placeholder="Enter phone number"
                />
              </div>
            )}

            <div className="two-factor-modal-buttons">
              <button
                type="button"
                className="two-factor-modal-btn two-factor-modal-btn-cancel"
                onClick={handleClose}
                disabled={loading || profileLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="two-factor-modal-btn two-factor-modal-btn-save"
                onClick={handleSave}
                disabled={loading || profileLoading}
              >
                {loading
                  ? requiresOtpBeforeSave
                    ? "Sending OTP..."
                    : "Saving..."
                  : requiresOtpBeforeSave
                    ? "Send OTP"
                    : "Save"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="two-factor-modal-title">Verify Phone Number</h2>
            <p className="two-factor-modal-subtitle">
              Enter the OTP sent to your mobile number.
            </p>

            <div className="two-factor-verified-phone">
              <span className="two-factor-verified-label">Phone number</span>
              <span className="two-factor-verified-value">{pendingPhone}</span>
            </div>

            <p className="two-factor-otp-note">
              If you didn&apos;t receive the OTP, please check the country code
              and the number once.
            </p>

            <div
              className="two-factor-otp-container"
              onPaste={handleOtpPaste}
            >
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`two-factor-otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleOtpChange(event, index)}
                  onKeyDown={(event) => handleOtpKeyDown(event, index)}
                  className="two-factor-otp-input"
                  disabled={loading}
                />
              ))}
            </div>

            {otpError ? (
              <p className="two-factor-otp-error">{otpError}</p>
            ) : null}

            <div className="two-factor-resend">
              {resendTimer > 0 ? (
                <span>
                  Resend OTP in {Math.floor(resendTimer / 60)}:
                  {String(resendTimer % 60).padStart(2, "0")}
                </span>
              ) : (
                <button
                  type="button"
                  className="two-factor-resend-btn"
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              type="button"
              className="two-factor-back-link"
              onClick={handleBackToSettings}
              disabled={loading}
            >
              Change phone number
            </button>

            <div className="two-factor-modal-buttons">
              <button
                type="button"
                className="two-factor-modal-btn two-factor-modal-btn-cancel"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="two-factor-modal-btn two-factor-modal-btn-save"
                onClick={handleVerifyAndSave}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify & Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TwoFactorAuthModal;
